import React, { FC, useCallback, useMemo, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import TextArea from "../../widgets/textArea";
import DropdownList from "../../widgets/DropdownList";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  ALLOWED_MAP_FILE_TYPES,
  BENCHMARK_CONTACTLIST,
  BENCHMARK_SCHEDULING,
  BENCHMARK_SCHEDULINGPRODUCTS,
  DEFAULT_OPTION,
  DEFAULT_PROPERTY_STATE,
  MAX_MAP_FILE_SIZE_BYTES,
  US_STATE_OPTIONS,
} from "../../constants/app.constants";
import {
  addSchedulingProperty,
  fetchProducts,
  fetchBenchmarkSchedulingCustomerContactList,
  fetchBenchmarkSchedulingProperties,
  fetchScheduledEditPropertyDetails,
  fetchScheduledStateValidation,
  fetchScheduledPropertyDetails,
} from "./scheduling.utils";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { SCHEDULING_DIALOGS } from "../../constants/dialog.constants";
import { getStoredValueWithCookieFallback } from "../../utils/common.utils";

type CustomerContactOption = {
  id: string;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
};

type CustomerContactApiRow = {
  col1_1?: string;
  col1_2?: string;
  col2_3?: string | number;
};

type PropertyOption = {
  id: string;
  name: string;
};

type ProductApiRow = {
  id?: number | string;
  product_name?: string;
};

type PropertyAutocompleteValue = PropertyOption | string | null;
type PropertyDetail = Record<string, unknown>;

type SchedulingAddPropertyModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isEditMode?: boolean;
  selectedProperty?: {
    id: number;
  } | null;
};

type ValidationErrors = {
  propertyName?: string;
  clientContact?: string;
  estimatedYards?: string;
  productColor?: string;
  propertyAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  propertyNotes?: string;
  uploadMap?: string;
};

const normalizeFieldKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const getStringField = (source: PropertyDetail, fieldNames: string[]) => {
  const normalizedSourceFields = Object.entries(source).map(([key, value]) => ({
    key: normalizeFieldKey(key),
    value,
  }));

  for (const fieldName of fieldNames) {
    const normalizedFieldName = normalizeFieldKey(fieldName);
    const matchedField = normalizedSourceFields.find(
      (field) => field.key === normalizedFieldName
    );
    const value = matchedField?.value;

    if (value !== undefined && value !== null) {
      return String(value).trim();
    }
  }

  return "";
};

const getPropertyDetail = (selectedPropertyDetails: unknown) => {
  const propertyDetail = Array.isArray(selectedPropertyDetails)
    ? selectedPropertyDetails[0]
    : selectedPropertyDetails;

  return propertyDetail && typeof propertyDetail === "object"
    ? (propertyDetail as PropertyDetail)
    : null;
};

const isPdfSource = (source: string, file?: File | null) =>
  file?.type === "application/pdf" ||
  /\.pdf(?:[?#]|$)/i.test(source);

const getPdfPreviewSrc = (source: string) =>
  source.includes("#") ? source : `${source}#page=1&toolbar=0`;

const isHttpUrl = (value: string) => /^https?:\/\//i.test(value);

const normalizeComparableText = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

const getFirstStringFieldByKey = (
  source: PropertyDetail,
  predicate: (normalizedKey: string) => boolean
) => {
  const matchedField = Object.entries(source).find(([key, value]) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return false;
    }

    return predicate(normalizeFieldKey(key));
  });

  return matchedField ? String(matchedField[1]).trim() : "";
};

const findContactOptionForPropertyDetail = (
  propertyDetail: PropertyDetail,
  clientContactOptions: CustomerContactOption[]
) => {
  const contactId = getStringField(propertyDetail, [
    "ClientContactID"
  ]) || getFirstStringFieldByKey(
    propertyDetail,
    (key) => key.includes("contact") && key.includes("id")
  );
  const contactFirstName = getStringField(propertyDetail, [
    "Contact_Name_First"
  ]);
  const contactLastName = getStringField(propertyDetail, [
    "Contact_Name_Last"
  ]);
  const contactFullNameFromParts = [contactFirstName, contactLastName]
    .filter(Boolean)
    .join(" ");
  const contactName = normalizeComparableText(
    getStringField(propertyDetail, [
    "Contact Name FL"
  ]) ||
    contactFullNameFromParts ||
    getFirstStringFieldByKey(
      propertyDetail,
      (key) =>
        key.includes("contact") &&
        (key.includes("name") || key.includes("full"))
    )
  );
  const contactEmail = normalizeComparableText(getStringField(propertyDetail, [
    "Email_Office"
  ]) || getFirstStringFieldByKey(
    propertyDetail,
    (key) => key.includes("contact") && key.includes("email")
  ));

  return clientContactOptions.find((option) => {
    const optionName = normalizeComparableText(option.name);
    const optionEmail = normalizeComparableText(option.email);

    if (contactId && String(option.id) === String(contactId)) {
      return true;
    }

    if (contactEmail && optionEmail === contactEmail) {
      return true;
    }

    return !!contactName && optionName === contactName;
  });
};

const isTruthyApiFlag = (value: unknown) =>
  value === true;

const getExistingPropertyLoaderMessage = (result: any) => {
  if (!isTruthyApiFlag(result?.response?.existingPropertyFound)) {
    return "";
  }

  return (
    result?.message ||
    result?.response?.message
  );
};

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolve());
    });
  });

const wait = (milliseconds: number) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const SchedulingAddPropertyModal: FC<SchedulingAddPropertyModalProps> = ({
  isOpen,
  onClose,
  isEditMode = false,
  selectedProperty = null,
}) => {
  const [propertyName, setPropertyName] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [clientContact, setClientContact] = useState("");
  const [estimatedYards, setEstimatedYards] = useState("");
  const [productColor, setProductColor] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState(DEFAULT_PROPERTY_STATE);
  const [zip, setZip] = useState("");
  const [propertyNotes, setPropertyNotes] = useState("");
  const [uploadMap, setUploadMap] = useState("");
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [clientContactEmail, setClientContactEmail] = useState("");
  const [clientContactFirstName, setClientContactFirstName] = useState("");
  const [clientContactLastName, setClientContactLastName] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("");
  const [isServiceAreaPopupOpen, setIsServiceAreaPopupOpen] = useState(false);
  const [highlightStateField, setHighlightStateField] = useState(false);
  const [isPropertyNameDropdownOpen, setIsPropertyNameDropdownOpen] =
    useState(false);
  const [isPropertyNameSearching, setIsPropertyNameSearching] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const organisationId = getStoredValueWithCookieFallback("ID", [
    "ID"
  ]);
  const customerContactId = getStoredValueWithCookieFallback("_k_Contact_ID", [
    "_k_Contact_ID"
  ]);
  const queryClient = useQueryClient();
  const shouldFetchEditPropertyDetails =
    isEditMode && !!selectedProperty?.id;
  const propertyDetailsRequestId =
    shouldFetchEditPropertyDetails && selectedProperty?.id
      ? String(selectedProperty.id)
      : selectedPropertyId;

  const { data: properties } = useQuery({
    queryKey: [BENCHMARK_SCHEDULING, organisationId, "property-dropdown"],
    queryFn: () => fetchBenchmarkSchedulingProperties(organisationId),
    enabled: isOpen && !!organisationId,
    refetchOnWindowFocus: false,
  });

  const propertyOptions = useMemo<PropertyOption[]>(() => {
    if (!properties || !Array.isArray(properties)) {
      return [];
    }

    const uniqueProperties = new Map<string, PropertyOption>();

    properties.forEach(
      (property: {
        PropertyID?: number | string;
        PropertyName?: string;
      }) => {
        const propertyId = String(property.PropertyID ?? "");
        const propertyLabel = (property.PropertyName ?? "").toString().trim();

        if (propertyId && propertyLabel && !uniqueProperties.has(propertyLabel)) {
          uniqueProperties.set(propertyLabel, {
            id: propertyId,
            name: propertyLabel,
          });
        }
      }
    );

    return Array.from(uniqueProperties.values());
  }, [properties]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isEditMode && selectedProperty) {
      setSelectedPropertyId("");
      setPropertyName("");
      setClientContact("");
      setClientContactEmail("");
      setClientContactFirstName("");
      setClientContactLastName("");
      setEstimatedYards("");
      setProductColor("");
      setPropertyAddress("");
      setCity("");
      setState(DEFAULT_PROPERTY_STATE);
      setZip("");
      setPropertyNotes("");
      setUploadMap("");
      setAttachmentFile(null);
      setHighlightStateField(false);
      setValidationErrors({});
    }
  }, [isEditMode, isOpen, selectedProperty]);

  React.useEffect(() => {
    if (!isOpen || !propertyOptions.length || !propertyName) {
      return;
    }

    const matchedProperty = propertyOptions.find(
      (option) =>
        option.name.toLowerCase() === propertyName.trim().toLowerCase()
    );

    if (matchedProperty) {
      setSelectedPropertyId(matchedProperty.id);
    }
  }, [isOpen, propertyName, propertyOptions]);

  const { data: customerContacts } = useQuery({
    queryKey: [BENCHMARK_CONTACTLIST, organisationId, "scheduling-add-property"],
    queryFn: () => fetchBenchmarkSchedulingCustomerContactList(organisationId),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const {
    data: products,
    isFetching: isFetchingProducts,
  } = useQuery({
    queryKey: [BENCHMARK_SCHEDULINGPRODUCTS, "property-popup"],
    queryFn: fetchProducts,
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  const { data: selectedPropertyDetails, isFetching: isFetchingPropertyDetails } = useQuery({
    queryKey: [
      shouldFetchEditPropertyDetails
        ? "SCHEDULED_EDIT_PROPERTY_DETAILS"
        : "SCHEDULED_PROPERTY_DETAILS",
      propertyDetailsRequestId,
    ],
    queryFn: () =>
      shouldFetchEditPropertyDetails
        ? fetchScheduledEditPropertyDetails(propertyDetailsRequestId)
        : fetchScheduledPropertyDetails(propertyDetailsRequestId),
    enabled: isOpen && !!propertyDetailsRequestId,
    refetchOnWindowFocus: false,
  });

  const {
    data: stateValidation = "",
    isFetching: isFetchingStateValidation,
  } = useQuery({
    queryKey: ["SCHEDULED_STATE_VALIDATION"],
    queryFn: fetchScheduledStateValidation,
    enabled: isOpen && isServiceAreaPopupOpen,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });



  React.useEffect(() => {
    if (!isOpen || !selectedPropertyDetails) {
      return;
    }

    const propertyDetail = getPropertyDetail(selectedPropertyDetails);
    if (!propertyDetail) {
      return;
    }

    const detailPropertyName = getStringField(propertyDetail, [
      "PropertyName"
    ]);
    const detailPropertyId = getStringField(propertyDetail, [
      "PropertyID"  
    ]);

    setPropertyName(detailPropertyName);
    setSelectedPropertyId((currentPropertyId) =>
      detailPropertyId || currentPropertyId
    );
    setEstimatedYards(
      getStringField(propertyDetail, [
        "EstimatedYards",
      ])
    );
    setProductColor(
      getStringField(propertyDetail, [
        "ProductColor",
      ])
    );
    setPropertyAddress(
      getStringField(propertyDetail, ["Address", "PropertyAddress", "address"])
    );
    setCity(getStringField(propertyDetail, ["City", "city"]));
    setState(
      getStringField(propertyDetail, ["State", "state"]) ||
        DEFAULT_PROPERTY_STATE
    );
    setZip(getStringField(propertyDetail, ["Zip", "PostalCode", "postalcode"]));
    setPropertyNotes(
      getStringField(propertyDetail, ["Notes", "PropertyNotes", "propertynotes"])
    );
    setUploadMap(getStringField(propertyDetail, ["Map", "CompanyMap", "map"]));
    setAttachmentFile(null);
    setIsPropertyNameDropdownOpen(false);
    setIsPropertyNameSearching(false);

    setValidationErrors((prev) => ({
      ...prev,
      propertyName: "",
      estimatedYards: "",
      productColor: "",
      propertyAddress: "",
      city: "",
      state: "",
      zip: "",
      propertyNotes: "",
      uploadMap: "",
    }));
  }, [isOpen, selectedPropertyDetails]);

  const clientContactOptions = useMemo<CustomerContactOption[]>(() => {
    if (!customerContacts || !Array.isArray(customerContacts)) {
      return [];
    }

    return customerContacts.map((contact: CustomerContactApiRow, index: number) => {
      const name = contact.col1_1 || "";
      const nameParts = name.trim().split(/\s+/);
      const firstName = nameParts.slice(0, -1).join(" ") || name;
      const lastName = nameParts.length > 1 ? nameParts.slice(-1).join(" ") : "";
      const email = contact.col1_2 || "";

      return {
        id: String(contact.col2_3 ?? index),
        name,
        email,
        firstName,
        lastName,
      };
    });
  }, [customerContacts]);

  const productOptions = useMemo<PropertyOption[]>(() => {
    if (!products || !Array.isArray(products)) {
      return [];
    }

    return products
      .map((product: ProductApiRow) => ({
        id: String(product.id ?? ""),
        name: (product.product_name ?? "").toString().trim(),
      }))
      .filter((product) => product.id && product.name);
  }, [products]);

  const lockedCustomerContact = useMemo(
    () =>
      customerContactId
        ? clientContactOptions.find(
            (option) => String(option.id) === String(customerContactId)
          ) || null
        : null,
    [clientContactOptions, customerContactId]
  );
  const effectiveClientContact = lockedCustomerContact?.id || clientContact;
  const effectiveClientContactEmail =
    lockedCustomerContact?.email || clientContactEmail;
  const effectiveClientContactFirstName =
    lockedCustomerContact?.firstName || clientContactFirstName;
  const effectiveClientContactLastName =
    lockedCustomerContact?.lastName || clientContactLastName;

  React.useEffect(() => {
    if (!isOpen || !lockedCustomerContact) {
      return;
    }

    setClientContact(lockedCustomerContact.id);
    setClientContactEmail(lockedCustomerContact.email);
    setClientContactFirstName(lockedCustomerContact.firstName);
    setClientContactLastName(lockedCustomerContact.lastName);
    setValidationErrors((prev) => ({ ...prev, clientContact: "" }));
  }, [isOpen, lockedCustomerContact]);

  React.useEffect(() => {
    if (!productColor || !productOptions.length) {
      return;
    }

    const hasSelectedProduct = productOptions.some(
      (option) => String(option.id) === String(productColor)
    );

    if (hasSelectedProduct) {
      return;
    }

    const matchedProduct = productOptions.find(
      (option) =>
        normalizeComparableText(option.name) ===
        normalizeComparableText(productColor)
    );

    if (matchedProduct) {
      setProductColor(matchedProduct.id);
    }
  }, [productColor, productOptions]);

  React.useEffect(() => {
    if (
      !isOpen ||
      lockedCustomerContact ||
      !selectedPropertyDetails ||
      !clientContactOptions.length
    ) {
      return;
    }

    const propertyDetail = getPropertyDetail(selectedPropertyDetails);
    if (!propertyDetail) {
      return;
    }

    const matchedContact = findContactOptionForPropertyDetail(
      propertyDetail,
      clientContactOptions
    );

    if (!matchedContact) {
      return;
    }

    setClientContact(matchedContact.id);
    setClientContactEmail(matchedContact.email);
    setClientContactFirstName(matchedContact.firstName);
    setClientContactLastName(matchedContact.lastName);
    setValidationErrors((prev) => ({ ...prev, clientContact: "" }));
  }, [clientContactOptions, isOpen, lockedCustomerContact, selectedPropertyDetails]);

  const mapPreviewSrc = useMemo(() => {
    if (attachmentFile) {
      return URL.createObjectURL(attachmentFile);
    }

    if (isHttpUrl(uploadMap)) {
      return uploadMap;
    }

    return "";
  }, [attachmentFile, uploadMap]);
  const uploadMapDisplayName =
    uploadMap && !isHttpUrl(uploadMap) ? uploadMap : "";
  const isMapPreviewPdf = mapPreviewSrc
    ? isPdfSource(mapPreviewSrc, attachmentFile)
    : false;

  React.useEffect(() => {
    return () => {
      if (attachmentFile && mapPreviewSrc && mapPreviewSrc.startsWith("blob:")) {
        URL.revokeObjectURL(mapPreviewSrc);
      }
    };
  }, [attachmentFile, mapPreviewSrc]);

  const selectedPropertyOption = useMemo(
    () =>
      propertyOptions.find((option) => option.id === selectedPropertyId) || null,
    [propertyOptions, selectedPropertyId]
  );

  const visiblePropertyOptions = useMemo(() => {
    if (!isPropertyNameSearching) {
      return propertyOptions;
    }

    const normalizedPropertyName = propertyName.trim().toLowerCase();

    if (!normalizedPropertyName) {
      return propertyOptions;
    }

    return propertyOptions.filter((option) =>
      option.name.toLowerCase().includes(normalizedPropertyName)
    );
  }, [isPropertyNameSearching, propertyName, propertyOptions]);

  const updatePropertySelection = useCallback(
    (selectedValue: string) => {
      const matchedProperty = propertyOptions.find(
        (option) =>
          option.name.toLowerCase() === selectedValue.trim().toLowerCase()
      );

      setPropertyName(selectedValue);
      setSelectedPropertyId(matchedProperty?.id || "");

      if (!matchedProperty) {
        setClientContact("");
        setClientContactEmail("");
        setClientContactFirstName("");
        setClientContactLastName("");
        setEstimatedYards("");
        setProductColor("");
        setPropertyAddress("");
        setCity("");
        setState(DEFAULT_PROPERTY_STATE);
        setZip("");
        setPropertyNotes("");
        setUploadMap("");
        setAttachmentFile(null);
      }

      setValidationErrors((prev) => ({ ...prev, propertyName: "" }));
    },
    [propertyOptions]
  );

  const onChangeFieldValues = useCallback(
    (
      id: string,
      updatedValue?: string[] | string | Date | undefined,
      updatedName?: string,
      parsedObj?: any
    ) => {
      if (id === "propertyName") {
        updatePropertySelection((updatedValue as string) || "");
      } else if (id === "clientContact") {
        const selectedValue = (updatedValue as string) || "";
        setClientContact(selectedValue === DEFAULT_OPTION ? "" : selectedValue);
        setClientContactEmail(selectedValue === DEFAULT_OPTION ? "" : parsedObj?.email || "");
        setClientContactFirstName(
          selectedValue === DEFAULT_OPTION ? "" : parsedObj?.firstName || ""
        );
        setClientContactLastName(
          selectedValue === DEFAULT_OPTION ? "" : parsedObj?.lastName || ""
        );
        setValidationErrors((prev) => ({ ...prev, clientContact: "" }));
      } else if (id === "estimatedYards") {
        setEstimatedYards((updatedValue as string) || "");
        setValidationErrors((prev) => ({ ...prev, estimatedYards: "" }));
      } else if (id === "productColor") {
        const selectedValue = (updatedValue as string) || "";
        setProductColor(selectedValue === DEFAULT_OPTION ? "" : selectedValue);
        setValidationErrors((prev) => ({ ...prev, productColor: "" }));
      } else if (id === "propertyAddress") {
        setPropertyAddress((updatedValue as string) || "");
        setValidationErrors((prev) => ({ ...prev, propertyAddress: "" }));
      } else if (id === "city") {
        setCity((updatedValue as string) || "");
        setValidationErrors((prev) => ({ ...prev, city: "" }));
      } else if (id === "state") {
        const selectedValue =
          ((updatedValue as string) || "") === DEFAULT_OPTION
            ? DEFAULT_PROPERTY_STATE
            : (updatedValue as string) || "";
        setState(selectedValue);
        setHighlightStateField(false);
        setIsServiceAreaPopupOpen(
          !!selectedValue && selectedValue !== DEFAULT_PROPERTY_STATE
        );
        setValidationErrors((prev) => ({ ...prev, state: "" }));
      } else if (id === "zip") {
        setZip((updatedValue as string) || "");
        setValidationErrors((prev) => ({ ...prev, zip: "" }));
      } else if (id === "propertyNotes") {
        setPropertyNotes((updatedValue as string) || "");
        setValidationErrors((prev) => ({ ...prev, propertyNotes: "" }));
      }
    },
    [updatePropertySelection]
  );

  const handleServiceAreaPopupClose = useCallback(() => {
    setIsServiceAreaPopupOpen(false);
    setHighlightStateField(true);

    window.setTimeout(() => {
      document.getElementById("state")?.focus();
    }, 0);
  }, []);

  const handleCancel = useCallback(() => {
    setPropertyName("");
    setSelectedPropertyId("");
    setClientContact("");
    setClientContactEmail("");
    setClientContactFirstName("");
    setClientContactLastName("");
    setEstimatedYards("");
    setProductColor("");
    setPropertyAddress("");
    setCity("");
    setState(DEFAULT_PROPERTY_STATE);
    setZip("");
    setPropertyNotes("");
    setUploadMap("");
    setAttachmentFile(null);
    setIsShowLoader(false);
    setLoaderMessage("");
    setIsServiceAreaPopupOpen(false);
    setHighlightStateField(false);
    setValidationErrors({});
    onClose();
  }, [onClose]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];

      if (!selectedFile) {
        setUploadMap("");
        setAttachmentFile(null);
        setValidationErrors((prev) => ({ ...prev, uploadMap: "" }));
        return;
      }

      if (!ALLOWED_MAP_FILE_TYPES.includes(selectedFile.type)) {
        setUploadMap("");
        setAttachmentFile(null);
        setValidationErrors((prev) => ({
          ...prev,
          uploadMap: SCHEDULING_DIALOGS.invalidMapFileType,
        }));
        return;
      }

      if (selectedFile.size > MAX_MAP_FILE_SIZE_BYTES) {
        setUploadMap("");
        setAttachmentFile(null);
        setValidationErrors((prev) => ({
          ...prev,
          uploadMap: SCHEDULING_DIALOGS.mapFileTooLarge,
        }));
        return;
      }

      setUploadMap(selectedFile.name);
      setAttachmentFile(selectedFile);
      setValidationErrors((prev) => ({ ...prev, uploadMap: "" }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    const errors: ValidationErrors = {};

    if (!propertyName.trim()) {
      errors.propertyName = SCHEDULING_DIALOGS.propertyNameRequired;
    }

    if (!effectiveClientContact) {
      errors.clientContact = SCHEDULING_DIALOGS.clientContactRequired;
    }

    if (!estimatedYards.trim()) {
      errors.estimatedYards = SCHEDULING_DIALOGS.estimatedYardsRequired;
    }

    if (!productColor) {
      errors.productColor = SCHEDULING_DIALOGS.productColorRequired;
    }

    if (!propertyAddress.trim()) {
      errors.propertyAddress = SCHEDULING_DIALOGS.propertyAddressRequired;
    }

    if (!city.trim()) {
      errors.city = SCHEDULING_DIALOGS.cityRequired;
    }

    if (!state.trim()) {
      errors.state = SCHEDULING_DIALOGS.stateRequired;
    }

    if (!zip.trim()) {
      errors.zip = SCHEDULING_DIALOGS.zipRequired;
    }

    if (!propertyNotes.trim()) {
      errors.propertyNotes = SCHEDULING_DIALOGS.propertyNotesRequired;
    }

    if (!uploadMap.trim() && !attachmentFile) {
      errors.uploadMap = SCHEDULING_DIALOGS.uploadMapRequired;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (state && state !== DEFAULT_PROPERTY_STATE) {
      setIsServiceAreaPopupOpen(true);
      return;
    }

    try {
      setIsShowLoader(true);
      setLoaderMessage("");
      setValidationErrors({});

      const selectedProductColorName =
        productOptions.find(
          (option) => String(option.id) === String(productColor)
        )?.name || productColor;

      const result = await addSchedulingProperty(
        organisationId,
        selectedPropertyId,
        propertyName,
        propertyAddress,
        effectiveClientContactFirstName,
        effectiveClientContactLastName,
        effectiveClientContact,
        estimatedYards,
        selectedProductColorName,
        productColor,
        city,
        state,
        zip,
        propertyNotes,
        attachmentFile,
        isEditMode,
        selectedProperty?.id
      );

      if (result?.status === "success") {
        const apiLoaderMessage = getExistingPropertyLoaderMessage(result);

        if (apiLoaderMessage) {
          setLoaderMessage(apiLoaderMessage);
          await waitForNextPaint();
          await wait(2000);
        }

        await queryClient.invalidateQueries({ queryKey: [BENCHMARK_SCHEDULING] });
        Swal.fire({
          customClass: {
            popup: "sweetAlert",
          },
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          title: isEditMode
            ? SCHEDULING_DIALOGS.propertyUpdatedSuccess
            : SCHEDULING_DIALOGS.propertyAddedSuccess,
        });
        handleCancel();
      } else {
        const apiLoaderMessage = getExistingPropertyLoaderMessage(result);

        if (apiLoaderMessage) {
          setLoaderMessage(apiLoaderMessage);
          await waitForNextPaint();
          await wait(2000);
          return;
        }

        Swal.fire({
          customClass: ".swal2-confirm",
          icon: "warning",
          title: result?.message || SCHEDULING_DIALOGS.propertyAddFailed,
        });
      }
    } catch (error) {
      console.error("Failed to add property", error);
      Swal.fire({
        customClass: ".swal2-confirm",
        icon: "warning",
        title: SCHEDULING_DIALOGS.propertyAddFailed,
      });
    } finally {
      setIsShowLoader(false);
      setLoaderMessage("");
    }
  }, [
    attachmentFile,
    city,
    effectiveClientContact,
    effectiveClientContactFirstName,
    effectiveClientContactLastName,
    estimatedYards,
    handleCancel,
    isEditMode,
    organisationId,
    productColor,
    productOptions,
    propertyAddress,
    selectedProperty,
    selectedPropertyId,
    propertyName,
    propertyNotes,
    queryClient,
    state,
    uploadMap,
    zip,
  ]);

  const serviceAreaMessage = stateValidation || "";

  return (
    <>
      <Modal
        show={isOpen}
        label={isEditMode ? "Edit Property" : "Add Property"}
        onClose={handleCancel}
        className="custom-modal scheduling-add-property-modal"
      >
      <div className="requestModalSection">
        <div className="modalForm">
          {isShowLoader || isFetchingPropertyDetails || isFetchingProducts ? (
            <Loader
              loaderStyle={{ margin: "auto", textAlign: "center" }}
              text={loaderMessage}
            />
          ) : null}
          <form>
            <div className="modal-body1 w-100">
           
              <div className="scheduling-modal-row mb-3">
                <div className="scheduling-modal-col scheduling-modal-col-100">
                  <label className="formFieldLabel scheduling-modal-label">
                    Property Name<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <Autocomplete<PropertyOption, false, false, true>
                      id="propertyName"
                      freeSolo
                      forcePopupIcon
                      fullWidth
                      openOnFocus
                      popupIcon={<KeyboardArrowDownIcon />}
                      open={
                        isPropertyNameDropdownOpen &&
                        (!isPropertyNameSearching ||
                          visiblePropertyOptions.length > 0)
                      }
                      onOpen={() => {
                        setIsPropertyNameDropdownOpen(true);
                        setIsPropertyNameSearching(false);
                      }}
                      onClose={() => {
                        setIsPropertyNameDropdownOpen(false);
                        setIsPropertyNameSearching(false);
                      }}
                      selectOnFocus
                      handleHomeEndKeys
                      options={visiblePropertyOptions}
                      value={selectedPropertyOption || propertyName || null}
                      inputValue={propertyName}
                      filterOptions={(options) => options}
                      getOptionLabel={(option: PropertyAutocompleteValue) =>
                        typeof option === "string" ? option : option?.name || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        typeof value !== "string" && option.id === value.id
                      }
                      onChange={(_, newValue: PropertyAutocompleteValue) => {
                        if (typeof newValue === "string") {
                          updatePropertySelection(newValue);
                          return;
                        }

                        updatePropertySelection(newValue?.name || "");
                      }}
                      onInputChange={(_, newInputValue, reason) => {
                        setIsPropertyNameDropdownOpen(reason !== "clear");
                        setIsPropertyNameSearching(reason === "input");
                        updatePropertySelection(newInputValue);
                      }}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          placeholder="Select or type Property Name"
                          size="small"
                          error={!!validationErrors.propertyName}
                          className={
                            validationErrors.propertyName
                              ? "scheduling-field-error"
                              : ""
                          }
                        />
                      )}
                    />
                  </div>
                  {validationErrors.propertyName ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.propertyName}
                    </div>
                  ) : null}
                </div>

                    <div className="scheduling-modal-col">
                  <label className="formFieldLabel scheduling-modal-label">
                    Client Contact<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <DropdownList
                      id="clientContact"
                      dropDownData={clientContactOptions}
                      onChange={onChangeFieldValues}
                      defaultOption="Select Client Contact"
                      selectedID={effectiveClientContact}
                      className={`form-select ${!effectiveClientContact ? "scheduling-select-placeholder" : ""} ${validationErrors.clientContact ? "scheduling-field-error" : ""}`}
                      optionTitleKey="email"
                      selectTitle={effectiveClientContactEmail}
                      hideDefaultOptionInList
                      disabled={!!lockedCustomerContact}
                    />
                  </div>
                  {validationErrors.clientContact ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.clientContact}
                    </div>
                  ) : null}
                  {effectiveClientContactEmail ? (
                    <div className="scheduling-contact-email">({effectiveClientContactEmail})</div>
                  ) : null}
                </div>

                
              </div>
          

              <div className="form-group mb-3">
                <label className="formFieldLabel scheduling-modal-label">
                  Property Address<span className="text-danger">*</span>
                </label>
                <div className="scheduling-modal-field scheduling-modal-field-full">
                  <TextField
                    id="propertyAddress"
                    onChange={onChangeFieldValues}
                    type="text"
                    value={propertyAddress}
                    className={`form-control ${validationErrors.propertyAddress ? "scheduling-field-error" : ""}`}
                  />
                </div>
                {validationErrors.propertyAddress ? (
                  <div className="scheduling-validation-text">
                    {validationErrors.propertyAddress}
                  </div>
                ) : null}
              </div>

              <div className="scheduling-modal-row mb-3">
                <div className="scheduling-modal-col">
                  <label className="formFieldLabel scheduling-modal-label">
                    City<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <TextField
                      id="city"
                      onChange={onChangeFieldValues}
                      type="text"
                      value={city}
                      className={`form-control ${validationErrors.city ? "scheduling-field-error" : ""}`}
                    />
                  </div>
                  {validationErrors.city ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.city}
                    </div>
                  ) : null}
                </div>

                <div className="scheduling-modal-col">
                  <label className="formFieldLabel scheduling-modal-label">
                    State<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <DropdownList
                      id="state"
                      dropDownData={US_STATE_OPTIONS}
                      onChange={onChangeFieldValues}
                      defaultOption="Select State"
                      selectedID={state}
                      className={`form-select ${
                        validationErrors.state || highlightStateField
                          ? "scheduling-field-error"
                          : ""
                      }`}
                    />
                  </div>
                  {validationErrors.state || highlightStateField ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.state ||
                        SCHEDULING_DIALOGS.outsideServiceArea}
                    </div>
                  ) : null}
                </div>

                <div className="scheduling-modal-col">
                  <label className="formFieldLabel scheduling-modal-label">
                    Zip<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <TextField
                      id="zip"
                      onChange={onChangeFieldValues}
                      type="text"
                      value={zip}
                      className={`form-control ${validationErrors.zip ? "scheduling-field-error" : ""}`}
                    />
                  </div>
                  {validationErrors.zip ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.zip}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="scheduling-modal-row mb-3">
            
               <div className="scheduling-modal-col scheduling-estimated-yards-col1">
                  <label className="formFieldLabel scheduling-modal-label">
                    Estimated Yards<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <TextField
                      id="estimatedYards"
                      onChange={onChangeFieldValues}
                      type="text"
                      value={estimatedYards}
                      className={`form-control ${validationErrors.estimatedYards ? "scheduling-field-error" : ""}`}
                    />
                  </div>
                  {validationErrors.estimatedYards ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.estimatedYards}
                    </div>
                  ) : null}
                </div>


               <div className="scheduling-modal-col scheduling-product-color-col1">
                  <label className="formFieldLabel scheduling-modal-label">
                    Product/Color<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <DropdownList
                      id="productColor"
                      dropDownData={productOptions}
                      onChange={onChangeFieldValues}
                      defaultOption="Select Product/Color"
                      selectedID={productColor}
                      className={`form-select ${!productColor ? "scheduling-select-placeholder" : ""} ${validationErrors.productColor ? "scheduling-field-error" : ""}`}
                      hideDefaultOptionInList
                    />
                  </div>
                  {validationErrors.productColor ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.productColor}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="scheduling-modal-row mb-3">
                
              <div  className="scheduling-modal-col">
                <label className="formFieldLabel scheduling-modal-label  ">
                  Property Notes<span className="text-danger">*</span>
                </label>
                <div className="scheduling-modal-field scheduling-modal-field-full">
                  <TextArea
                    id="propertyNotes"
                    className={`form-control textareaInput scheduling-notes-input ${validationErrors.propertyNotes ? "scheduling-field-error" : ""}`}
                    onChange={onChangeFieldValues}
                    value={propertyNotes}
                    rows={4}
                  />
                </div>
                {validationErrors.propertyNotes ? (
                  <div className="scheduling-validation-text">
                    {validationErrors.propertyNotes}
                  </div>
                ) : null}
              </div>
               <div className="scheduling-modal-col">
                  <label className="formFieldLabel scheduling-modal-label">
                    Upload Map<span className="text-danger">*</span>
                  </label>
                  <div className="scheduling-modal-field">
                    <input
                      type="file"
                      className={`form-control scheduling-file-input ${validationErrors.uploadMap ? "scheduling-field-error" : ""}`}
                      onChange={handleFileChange}
                    />
                   {mapPreviewSrc ? (
                    <div className="scheduling-map-preview-wrapper">
                      {isMapPreviewPdf ? (
                        <iframe
                          src={getPdfPreviewSrc(mapPreviewSrc)}
                          title="Property Map PDF Preview"
                          className="scheduling-map-preview scheduling-map-pdf-preview"
                        />
                      ) : (
                        <>
                          <img
                            src={mapPreviewSrc}
                            alt="Property Map"
                            className="scheduling-map-preview"
                          />

                          {uploadMapDisplayName ? (
                            <>
                            <small className="scheduling-upload-name">
                              {uploadMapDisplayName}
                            </small>
                            <a className="btn btn-link text-success btn-sm p-0" href={mapPreviewSrc} target="_blank" rel="noopener noreferrer">
                              View
                            </a></>
                          ) : null}
                        </>
                      )}
                    </div>
                  ) : null}
                  </div>
                  {validationErrors.uploadMap ? (
                    <div className="scheduling-validation-text">
                      {validationErrors.uploadMap}
                    </div>
                  ) : null}
                </div>
              </div>

            

              <div className="scheduling-modal-actions">
                <button
                  type="button"
                  className="btn cancelPwdBtn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn submitBtn"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>

      <Modal
        show={isServiceAreaPopupOpen}
        label="Warning"
        onClose={handleServiceAreaPopupClose}
        className="custom-modal scheduling-service-area-modal warningModal"
      >
        <div className="requestModalSection">
          <div className="modalForm">
            <div className="modal-body1 w-100">
              <div className="warningContent">
                <div className="warningIcon">
                  <svg xmlns="http://www.w3.org/2000/svg" height="34px" viewBox="0 -960 960 960" width="24px" fill="#db4005"><path d="M440-520h80v-200h-80v200Zm40 160q17 0 28.5-11.5T520-400q0-17-11.5-28.5T480-440q-17 0-28.5 11.5T440-400q0 17 11.5 28.5T480-360Zm0 174q122-112 181-203.5T720-552q0-109-69.5-178.5T480-800q-101 0-170.5 69.5T240-552q0 71 59 162.5T480-186Zm0 106Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Zm0-480Z"/></svg>
                </div>
                <div className="warningMessage">
                  <h5 className="fw-bold">This property is outside our service area.</h5>
                  <p>We are currently unable to provide service at this location. Please <a href="#">contact our office for assistance</a> or to discuss available options.</p>
                </div>
              </div>
              {isFetchingStateValidation ? <Loader /> : null}
              {/* <p className="mb-4">{serviceAreaMessage}</p> */}
              <div className="scheduling-modal-actions">
                <button
                  type="button"
                  className="btn cancelPwdBtn"
                  onClick={handleServiceAreaPopupClose}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SchedulingAddPropertyModal;
